package com.macrofocus.eflux.ui

import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.Property
import com.macrofocus.common.selection.*
import com.macrofocus.eflux.context.ActiveTooltip
import com.macrofocus.eflux.context.DisplayTooltip
import com.macrofocus.eflux.network.*
import com.macrofocus.eflux.network.logical.LogicalBranchId
import com.macrofocus.eflux.network.logical.LogicalBusId
import com.macrofocus.eflux.ui.svg.ResizableSVG
import js.objects.unsafeJso
import org.mkui.interaction.InteractionMode
import org.mkui.react.useSelection
import org.mkui.react.useSingleSelection
import react.*
import svgpanzoom.SvgPanZoomContext
import web.dom.ElementId
import web.geometry.DOMMatrix
import web.geometry.DOMMatrixReadOnly
import web.html.HTMLDivElement
import web.mouse.MouseEvent
import web.svg.SVGElement
import web.svg.SVGSVGElement

external interface TooltipContainerProps : Props {
    var efluxNetwork: Property<EFluxNetwork?>
    var graphicalNetwork: Property<GraphicalNetwork?>
    var tooltipNodes: Property<Array<NodeField<*>>>
    var tooltipLinks: Property<Array<LinkField<*>>>
    var background: String?
    var url: String
    var divRef: RefObject<HTMLDivElement>
    var svgRef: RefObject<SVGElement>
    var svgsvg: MutableProperty<SVGSVGElement?>
    var svgPanZoomContext: MutableProperty<SvgPanZoomContext?>
    var pointProbing: MutableSingleSelection<LogicalBusId>
    var pathProbing: MutableSingleSelection<LogicalBranchId>
    var pointSelection: MutableSelection<LogicalBusId>
    var pathSelection: MutableSelection<LogicalBranchId>
    var dnd: Boolean
    var tooltips: Boolean
    var svgMatrix: MutableProperty<DOMMatrixReadOnly?>
    var activeTooltip: MutableProperty<ActiveTooltip>
    var displayTooltip: DisplayTooltip
    var interactionMode: MutableProperty<InteractionMode>
}

const val USE_OFFSET = false

val TooltipContainer = FC<TooltipContainerProps> { props ->
//    val classes: TooltipStyle = useTooltipStyles()

    /**
     * Obtains the model used for probing (aka mouse over).
     *
     * @return the model used for probing.
     */
    val (pointProbing, _) = useState { props.pointProbing }
    val (pathProbing, _) = useState { props.pathProbing }

    val (pointSelection, _) = useState { props.pointSelection }
    val (pathSelection, _) = useState { props.pathSelection }

    val (controller, _) = useState { Controller(props.activeTooltip) }

    val (probedNode, _) = useSingleSelection(pointProbing)
    val (probedLink, _) = useSingleSelection(pathProbing)

    val (selectedNodes, _) = useSelection(props.pointSelection)
    val (selectedLinks, _) = useSelection(props.pathSelection)

//    val (_, setOnPanZoom) = useState {
//        @Suppress("KotlinDeprecation")
//        null as Any?
//    }

    // Whether an object is currently being dragged
    val (getDragging, setDragging) = useState { false }

    val (draggingOnMouseMoveSelection, _) = useState {
        val selection: MutableSingleSelection<(event: react.dom.events.MouseEvent<*, *>) -> Unit> =
            SimpleSingleSelection(); selection
    }
    val (draggingOnMouseUpSelection, _) = useState {
        val selection: MutableSingleSelection<(event: react.dom.events.MouseEvent<*, *>) -> Unit> =
            SimpleSingleSelection(); selection
    }

    useEffectOnce {
        pointProbing.addSingleSelectionListener(object : SingleSelectionListener<LogicalBusId> {
            override fun selectionChanged(event: SingleSelectionEvent<LogicalBusId>) {
                val previous = props.efluxNetwork.value?.graphicalNodes?.get(event.previousSelection)
                val current = props.efluxNetwork.value?.graphicalNodes?.get(event.currentSelection)
                previous?.probed = false
                current?.probed = true
            }
        })

        pathProbing.addSingleSelectionListener(object : SingleSelectionListener<LogicalBranchId> {
            override fun selectionChanged(event: SingleSelectionEvent<LogicalBranchId>) {
                val previous = props.efluxNetwork.value?.graphicalLinks?.get(event.previousSelection)
                val current = props.efluxNetwork.value?.graphicalLinks?.get(event.currentSelection)
                previous?.probed = false
                current?.probed = true
            }
        })

        pointSelection.addSelectionListener(object : SelectionListener<LogicalBusId> {
            override fun selectionChanged(event: SelectionEvent<LogicalBusId>) {
                for (node in event.affected) {
                    val n = props.efluxNetwork.value?.graphicalNodes?.get(node)
                    n?.selected = event.model.isSelected(node)
                }
            }
        })

        pathSelection.addSelectionListener(object : SelectionListener<LogicalBranchId> {
            override fun selectionChanged(event: SelectionEvent<LogicalBranchId>) {
                for (link in event.affected) {
                    val l = props.efluxNetwork.value?.graphicalLinks?.get(link)
                    l?.selected = event.model.isSelected(link)
                }
            }
        })
    }

    ResizableSVG {
        url = props.url
        background = props.background
        divRef = props.divRef
        svgRef = props.svgRef
        svgElementChanged = { svgsvgElement: SVGSVGElement?, svgPanZoomContext: SvgPanZoomContext? ->
            props.svgsvg.value = svgsvgElement
            props.svgPanZoomContext.value = svgPanZoomContext
        }
        onZoom = { zoom ->
            if (zoom is Double) {
                val lod = props.svgsvg.value?.getElementById(ElementId("lod")) as SVGElement?
                if (lod != null) {
                    if (zoom > 1.5) {
                        lod.style.visibility = "visible"
                    } else {
                        lod.style.visibility = "hidden"
                    }
                }
            }
        }
        onUpdate = { ctm ->
            if (ctm is DOMMatrixReadOnly) {
                props.svgMatrix.value = ctm
            } else if (ctm is DOMMatrix) {
                props.svgMatrix.value = ctm
            } else if (ctm is SVGMatrix) {
                props.svgMatrix.value = ctm
            } else if (ctm is org.w3c.dom.DOMMatrix) {
                props.svgMatrix.value = ctm.unsafeCast<DOMMatrixReadOnly>()
            } else {
                println("Unknown matrix class $ctm ${ctm::class}")
//                val unsafeCast = ctm.unsafeCast<DOMMatrixReadOnly>()
//                println("${unsafeCast.a},${unsafeCast.b},${unsafeCast.c},${unsafeCast.d}")
//                props.svgMatrix.value = unsafeCast
            }
        }

        // Add interactivity
        onMouseEnterFunction = {
            null
        }

        onMouseMoveFunction = {
            if (getDragging) {
                if (draggingOnMouseMoveSelection.selected != null) {
                    draggingOnMouseMoveSelection.selected!!(it)
                }
            } else {
                val eFluxNetwork = props.efluxNetwork.value
                val graphicalNetwork = props.graphicalNetwork.value
                val mouseEvent = it.asDynamic()
                if (eFluxNetwork != null && graphicalNetwork != null && mouseEvent.nativeEvent is MouseEvent) {
                    controller.processMouseSelection(
                        InteractionMode.Selection, eFluxNetwork, mouseEvent.nativeEvent,
                        graphicalNetwork, pointProbing, pathProbing
                    )
                }
            }
        }

        onMouseDownFunction = {
            if (getDragging) {
//                    getDraggingOnMouseDownFunction.invoke(it)
            } else {
                val eFluxNetwork = props.efluxNetwork.value
                val graphicalNetwork = props.graphicalNetwork.value
                val mouseEvent = it.asDynamic()
                if (eFluxNetwork != null && graphicalNetwork != null && mouseEvent.nativeEvent is MouseEvent) {
                    val svgPanZoomContext = props.svgPanZoomContext.value

                    if (svgPanZoomContext != null && it.detail == 2) {
                        // Double-click

                        val nativeEvent: MouseEvent = mouseEvent.nativeEvent
                        val closestNode: EFluxNode? = controller.findPoint(eFluxNetwork, nativeEvent)

                        println("Current pan: " + svgPanZoomContext.getPan().x + "," + svgPanZoomContext.getPan().y)

//                        run {
//                            val node: EFluxNode = eFluxNetwork.graphicalNodes[logicalBusIdOf("D4TRSN1")]!!
//                            val bbox = node.anchorEl.getBBox()
//                            println("D4TRSN1: ${bbox.x},${bbox.y},${bbox.width},${bbox.height}")
//                        }

//                        run {
//                            val node: EFluxNode = eFluxNetwork.graphicalNodes[logicalBusIdOf("IGORM11")]!!
//                            val bbox = node.anchorEl.getBBox()
//                            println("IGORM11: ${bbox.x},${bbox.y},${bbox.width},${bbox.height}")
//                        }

//                        run {
//                            val node: EFluxNode = eFluxNetwork.graphicalNodes[logicalBusIdOf("SLITTA2")]!!
//                            val bbox = node.anchorEl.getBBox()
//                            println("SLITTA2: ${bbox.x},${bbox.y},${bbox.width},${bbox.height}")
//                        }

                        if (false && closestNode != null) {
                            val pan = true
                            if (pan) {
                                run {
                                    val svg = graphicalNetwork.svg
                                    val bbox = svg.getBBox()
                                    val bounding = svg.getBoundingClientRect()

//                                    println("svg: ${svg.x},${svg.y},${svg.width},${svg.height}")
//                                    println("bbox: ${bbox.x},${bbox.y},${bbox.width},${bbox.height}")
//                                    println("bounding: ${bounding.x},${bounding.y},${bounding.width},${bounding.height}")
                                }

                                val svg = closestNode!!.anchorEl
                                val bbox = svg.getBBox()
                                val bounding = svg.getBoundingClientRect()

                                println("bbox2: ${bbox.x},${bbox.y},${bbox.width},${bbox.height}")
                                println("bounding2: ${bounding.x},${bounding.y},${bounding.width},${bounding.height}")

                                val point = closestNode!!.screenPanZoomCenter
                                val pan = graphicalNetwork.svg.createSVGPoint()
                                pan.x = point.x / svgPanZoomContext.getZoom()
                                pan.y = point.y / svgPanZoomContext.getZoom()
//                                point.x = -(bbox.x - (graphicalNetwork.svg.getBBox().width / 2.0))
//                                point.y = -(bbox.y - (graphicalNetwork.svg.getBBox().height / 2.0))
//
                                println("Panning to ${point.x},${point.y}")

                                svgPanZoomContext.pan(pan)

                                println("New pan: " + svgPanZoomContext.getPan().x + "," + svgPanZoomContext.getPan().y)
                            } else {
                                //                            val e_box = closestNode.centerSVG
                                val e_bounding = closestNode!!.boundingClientRect
                                val svg = graphicalNetwork.svg
                                val svg_bounding = svg.getBoundingClientRect();
                                //                            val svg_box = svg.getBBox();
                                //                            val svgX = e_box.x
                                //                            val svgY = e_box.y
                                //                            val pan = closestNode.svgToScreen(svgX, svgY)
                                //                            val panX = svgX
                                //                            val panY = svgY
                                //                            val panX = (svg_box.width / 2.0) + (e_box.x / svg_box.width)
                                //                            val panY = (svg_box.height / 2.0) + (e_box.y / svg_box.height)
                                //                            println("Result $panX,$panY")
                                //                            svgPanZoomContext.zoomAtPoint(svgPanZoomContext.getZoom() * 1.2, object {
                                //                                val x = panX
                                //                                val y = panY
                                //                            }, true)
                                //                            svgPanZoomContext.zoom(1.0)
                                //                            svgPanZoomContext.pan(object {
                                //                                val x = panX
                                //                                val y = panY
                                //                            })

                                //                            var point = SvgUtils.getEventPoint(evt, this.svg).matrixTransform(
                                //                                this.svg.getScreenCTM().inverse()
                                //                            );

                                // The event in screen coordinates
                                val screenPoint = svg.createSVGPoint()
                                screenPoint.x = nativeEvent.clientX.toDouble()
                                screenPoint.y = nativeEvent.clientY.toDouble()

                                // The event in SVG coordinates
                                val svgPoint = screenPoint.matrixTransform(svg.getScreenCTM()!!.inverse())
                                println("svgPoint: ${svgPoint.x},${svgPoint.y}")

                                // The node in SVG coordinates
                                println("bounding: ${e_bounding.x},${e_bounding.y}")

                                val svgPoint2 = svg.createSVGPoint()
                                println("svg_bounding: ${svg_bounding.x},${svg_bounding.y},${svg_bounding.width},${svg_bounding.height}")
                                //                            println("svg_bbox: ${svg_box.x},${svg_box.y},${svg_box.width},${svg_box.height}")
                                svgPoint2.x = e_bounding.x - svg_bounding.x
                                svgPoint2.y = e_bounding.y - svg_bounding.y

                                println("svgPoint: ${svgPoint2.x},${svgPoint2.y}")
                                //                            val p2 = svgPoint2.matrixTransform(svg.getScreenCTM()!!)
                                //                            println("p2: ${p2.x},${p2.y}")

                                //                            val panX = svgPoint.x - (svg_box.width / 2.0)
                                //                            val panY = svgPoint.y - (svg_box.height / 2.0)
                                svgPanZoomContext.zoomAtPoint(svgPanZoomContext.getZoom() * 1.2, object {
                                    val x = svgPoint2.x
                                    val y = svgPoint2.y
                                }, true)
                                //                            svgPanZoomContext.pan(object {
                                //                                val x = panX
                                //                                val y = panY
                                //                            })
                            }
                        } else {
                            val sizes = svgPanZoomContext.getSizes()
                            val viewBox = sizes.viewBox
//                            val x = viewBox.x.unsafeCast<Double>()
//                            val y = viewBox.y.unsafeCast<Double>()
//                            val width = viewBox.width.unsafeCast<Double>()
//                            val height = viewBox.height.unsafeCast<Double>()
//                            val panX = x + (width / 2.0)
//                            val panY = y + (height / 2.0)
                            val panX = 0.0
                            val panY = 0.0
//                            println("X: $x, Y: $y, Width: $width, Height: $height")
                            println("Pan: ${svgPanZoomContext.getPan().x},${svgPanZoomContext.getPan().y}")
                            val point = unsafeJso<dynamic> {
                                x = panX
                                y = panY
                            }
                            svgPanZoomContext.zoom(1.0)
                            svgPanZoomContext.pan(point)

                            // svgPanZoomContext.reset()
                        }
                    } else {
                        controller.processMouseSelection(
                            props.interactionMode.value, eFluxNetwork, mouseEvent.nativeEvent,
                            graphicalNetwork, pointSelection, pathSelection
                        )
                    }
                }
            }
        }

        onMouseUpFunction = {
            if (getDragging) {
                if (draggingOnMouseUpSelection.selected != null) {
                    draggingOnMouseUpSelection.selected!!(it)
                }
            }
        }

        onMouseOutFunction = {
//                pointProbing.clearSelection()
//                pathProbing.clearSelection()
        }

        if (pointSelection.isActive || pathSelection.isActive) {
            CloseAllTooltipsButton {
                onClose = {
                    setDragging(true)
                    pathSelection.clearSelection()
                    pointSelection.clearSelection()
                    setDragging(false)
                }
            }
        }
    }

    if (props.tooltips) {
        if (props.displayTooltip == DisplayTooltip.Selection) {
            selectedNodes.forEach { n ->
                val node = props.efluxNetwork.value?.graphicalNodes?.get(n)
                if (node != null) {
                    val getScreenX = node.screenX
                    val getScreenY = node.screenY
                    val getClientOffsetX = node.clientOffsetX
                    val getClientOffsetY = node.clientOffsetY
                    val setClientClientX: ((Double) -> Unit) = { node.clientOffsetX = it }
                    val setClientClientY: ((Double) -> Unit) = { node.clientOffsetY = it }

                    TooltipPopper {
                        anchorEl = node.anchorEl
                        placement = node.placement
                        this.getScreenX = getScreenX
                        this.getClientOffsetX = getClientOffsetX
                        this.setClientOffsetX = setClientClientX
                        this.getScreenY = getScreenY
                        this.getClientOffsetY = getClientOffsetY
                        this.setClientOffsetY = setClientClientY
                        this.getGlobalDragging = getDragging
                        this.setGlobalDragging = setDragging
                        this.draggingOnMouseMoveSelection = draggingOnMouseMoveSelection
                        this.draggingOnMouseUpSelection = draggingOnMouseUpSelection
                        pointerEvent = props.dnd || !getGlobalDragging
                        dnd = props.dnd
                        //                    this.classes = classes

                        Tooltip {
                            onEnter = {
                                pathProbing.clearSelection()
                                pointProbing.setSelectedElement(node.id)
                            }
                            onClose = { pointSelection.setSelectedState(node.id, false) }
                            //                        this.classes = classes
                            TooltipHeader {
                                title = node.fullName
                                this.getX = getScreenX
                                this.setX = setClientClientX
                                this.getY = getScreenY
                                this.setY = setClientClientY
                                //                            this.classes = classes
                            }
                            if (node.bus != null) {
                                TooltipContent {
                                    //                            attrs.classes = classes

                                    for (column in props.tooltipNodes.value) {
                                        TooltipItem {
                                            //                                    attrs.classes = classes

                                            TooltipCell {
                                                //                                        attrs.classes = classes

                                                +column.longName
                                            }
                                            TooltipNumber {
                                                //                                        attrs.classes = classes

                                                +(column.getFormattedValue(node) ?: "")
                                            }
                                        }
                                    }
                                }
                            } else {
                                TooltipContent {
                                    //                            attrs.classes = classes

                                    TooltipItem {
                                        //                                attrs.classes = classes
                                        +"No Data"
                                    }
                                }
                            }
                        }
                    }
                }
            }

            selectedLinks.forEach { l ->
                val link = props.efluxNetwork.value?.graphicalLinks?.get(l)
                if (link != null) {
                    val getScreenX = link.screenX
                    val getScreenY = link.screenY
                    val getClientOffsetX = link.clientOffsetX
                    val getClientOffsetY = link.clientOffsetY
                    val setClientOffsetX: ((Double) -> Unit) = { link.clientOffsetX = it }
                    val setClientOffsetY: ((Double) -> Unit) = { link.clientOffsetY = it }

                    TooltipPopper {
                        anchorEl = link.anchorEl
                        placement = link.placement
                        this.getScreenX = getScreenX
                        this.getClientOffsetX = getClientOffsetX
                        this.setClientOffsetX = setClientOffsetX
                        this.getScreenY = getScreenY
                        this.getClientOffsetY = getClientOffsetY
                        this.setClientOffsetY = setClientOffsetY
                        this.getGlobalDragging = getDragging
                        this.setGlobalDragging = setDragging
                        this.draggingOnMouseMoveSelection = draggingOnMouseMoveSelection
                        this.draggingOnMouseUpSelection = draggingOnMouseUpSelection
                        pointerEvent = props.dnd || !getGlobalDragging
                        dnd = props.dnd
                        //                    this.classes = classes
                        Tooltip {
                            onEnter = {
                                pointProbing.clearSelection()
                                pathProbing.setSelectedElement(link.id)
                            }
                            onClose = { pathSelection.setSelectedState(link.id, false) }
                            //                        this.classes = classes
                            if (link.branch != null) {
                                TooltipHeader {
                                    title = link.longName
                                    this.getX = getScreenX
                                    this.setX = setClientOffsetX
                                    this.getY = getScreenY
                                    this.setY = setClientOffsetY
                                    //                                this.classes = classes
                                }
                                TooltipContent {
                                    //                            attrs.classes = classes

                                    for (column in props.tooltipLinks.value) {
                                        TooltipItem {
                                            //                                    attrs.classes = classes
                                            TooltipCell {
                                                //                                        attrs.classes = classes
                                                +column.longName
                                            }
                                            TooltipNumber {
                                                //                                        attrs.classes = classes
                                                +(column.getFormattedValue(link) ?: "")
                                            }
                                        }

                                    }
                                }
                            } else {
                                TooltipHeader {
                                    //                            attrs.classes = classes
                                    title = link.longName
                                }
                                TooltipContent {
                                    //                            attrs.classes = classes
                                    TooltipItem {
                                        //                                attrs.classes = classes
                                        +"No Data"
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        probedNode?.apply {
            val n = this
            if (!selectedNodes.contains(n)) {
                val node = props.efluxNetwork.value?.graphicalNodes?.get(n)
                if (node != null) {
                    val getScreenX = node.screenX
                    val getScreenY = node.screenY

                    TooltipPopper {
                        //                attrs.classes = classes
                        anchorEl = node.anchorEl
                        placement = node.placement
                        this.getScreenX = getScreenX
                        this.getScreenY = getScreenY
                        this.draggingOnMouseMoveSelection = draggingOnMouseMoveSelection
                        this.draggingOnMouseUpSelection = draggingOnMouseUpSelection
                        pointerEvent = false
                        dnd = props.dnd

                        Tooltip {
                            //                    attrs.classes = classes
                            TooltipHeader {
                                //                        attrs.classes = classes
                                title = node.fullName
                                this.getX = getScreenX
                                this.setX = null
                                this.getY = getScreenY
                                this.setY = null
                            }
                            if (node.bus != null) {
                                TooltipContent {
                                    //                            attrs.classes = classes
                                    for (column in props.tooltipNodes.value) {
                                        TooltipItem {
                                            //                                    attrs.classes = classes
                                            TooltipCell {
                                                //                                        attrs.classes = classes
                                                +column.longName
                                            }
                                            TooltipNumber {
                                                //                                        attrs.classes = classes
                                                +(column.getFormattedValue(node) ?: "")
                                            }
                                        }
                                    }
                                }
                            } else {
                                TooltipContent {
                                    //                            attrs.classes = classes
                                    TooltipItem {
                                        //                                attrs.classes = classes
                                        +"No Data"
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }


        if (props.displayTooltip == DisplayTooltip.Probing || props.displayTooltip == DisplayTooltip.Selection) {
            probedLink?.apply {
                val l = this
                if (!selectedLinks.contains(l)) {
                    val link = props.efluxNetwork.value?.graphicalLinks?.get(l)
                    if (link != null) {
                        val getScreenX = link.screenX
                        val getScreenY = link.screenY

                        TooltipPopper {
                            //                attrs.classes = classes
                            anchorEl = link.anchorEl
                            placement = link.placement
                            this.getScreenX = getScreenX
                            this.getScreenY = getScreenY
                            this.draggingOnMouseMoveSelection = draggingOnMouseMoveSelection
                            this.draggingOnMouseUpSelection = draggingOnMouseUpSelection
                            pointerEvent = false
                            dnd = props.dnd

                            Tooltip {
                                //                    attrs.classes = classes
                                if (link.branch != null) {
                                    TooltipHeader {
                                        //                            attrs.classes = classes
                                        title = link.longName
                                        this.getX = getScreenX
                                        this.setX = null
                                        this.getY = getScreenY
                                        this.setY = null
                                    }
                                    TooltipContent {
                                        //                            attrs.classes = classes
                                        for (column in props.tooltipLinks.value) {
                                            //                                attrs.classes = classes
                                            TooltipItem {
                                                //                                    attrs.classes = classes
                                                TooltipCell {
                                                    //                                        attrs.classes = classes
                                                    +column.longName
                                                }
                                                TooltipNumber {
                                                    //                                        attrs.classes = classes
                                                    +(column.getFormattedValue(link) ?: "")
                                                }
                                            }

                                        }
                                    }
                                } else {
                                    TooltipHeader {
                                        //                            attrs.classes = classes
                                        title = link.longName
                                    }
                                    TooltipContent {
                                        //                            attrs.classes = classes
                                        TooltipItem {
                                            //                                attrs.classes = classes
                                            +"No Data"
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

//external interface TooltipStyle {
//    val popper: String
//    val passivePopper: String
//    val draggingPopper: String
//    val tooltip: String
//    val tooltipHeader: String
//    val tooltipHeaderTypography: String
//    val tooltipBody: String
//    val tooltipContent: String
//    val tooltipItem: String
//    val tooltipCell: String
//    val tooltipNumber: String
//}

//private val useTooltipStyles: () -> TooltipStyle = makeStyles {
//    "popper" {
//        pointerEvents = PointerEvents.auto
//    }
//    "passivePopper" {
//        pointerEvents = PointerEvents.none
//    }
//    "draggingPopper" {
//        pointerEvents = PointerEvents.auto
//        opacity = 0.5
//    }
//    "tooltip" {
//        backgroundColor = theme.palette.background.paper.withAlpha(0.7)
//        padding = 0.px.value
////        maxWidth = 15.vw
//    }
//    "tooltipHeader" {
////        marginBottom = theme.spacing(0.5)
//        padding = 4.px.value
//    }
//    "tooltipHeaderTypography" {
////        fontWeight = FontWeight.bold
//        fontSize = 0.9.rem
//        lineHeight = LineHeight("1.1")
//        textAlign = TextAlign.center
//    }
//    "tooltipBody" {
//    }
//    "tooltipContent" {
////        padding = 0.px.value
//        paddingTop = 0.px
//        paddingBottom = 0.px
//        paddingLeft = 10.px
//        paddingRight = 10.px
//        lastChild(ruleSet {
//            paddingBottom = 4.px
//        })
//    }
//    "tooltipItem" {
//        marginBottom = theme.spacing(0)
//        paddingTop = 0.px
//        paddingBottom = 0.px
//    }
//    "tooltipCell" {
//        fontSize = 0.75.rem
//        padding = 0.px.value
//        paddingRight = 4.px
//        borderBottom = "none"
//        lastChild(ruleSet {
//            paddingRight = 0.px
//        })
//    }
//    "tooltipNumber" {
//        fontSize = 0.75.rem
//        padding = 0.px.value
//        borderBottom = "none"
//        lastChild(ruleSet {
//            paddingRight = 0.px
//        })
//    }
//}

